import { useEffect, useState } from "react";
import { translate } from "app/filters/i18nFilter";
import { useDispatch } from "react-redux";
import { Button } from "@similarweb/ui-components";
import { AssetsService } from "app/services/AssetsService";
import { getHubspotStatusThunk } from "app/pages/sales-intelligence/sub-modules/management/store/effects/hubspot/status";
import { getSalesforceStatusThunk } from "app/pages/sales-intelligence/sub-modules/management/store/effects/salesforce/status";
import { fetchCrmListsThunk } from "app/pages/sales-intelligence/sub-modules/management/store/effects/crmLists";
import type { Integration } from "app/pages/sales-intelligence/sub-modules/management/types";
import type { CrmSyncStatusName } from "../types";
import { getCrmName } from "app/pages/sales-intelligence/utils/crm";
import { StyledCrmIntegratedDescription } from "../styles";
import { StyledUnmatchedEmail, StyledUnmatchedEmailImageContainer } from "./styles";

const getCrmStatusMap = { salesforce: getSalesforceStatusThunk, hubspot: getHubspotStatusThunk };

type UnmatchedEmailProps = { crmSyncStatusName: CrmSyncStatusName };

export const UnmatchedEmail = ({ crmSyncStatusName: crm }: UnmatchedEmailProps) => {
    const [authWindow, setAuthWindow] = useState<Window | null>(null);
    const dispatch = useDispatch();

    const crmName = getCrmName(crm as Integration);

    const getAuthWindowUrl = () => {
        return `/sales-api/sw-connector/${crm}/verify-user`;
    };

    const getAuthWindowFeatures = () => {
        const windowWidth = 600;
        const windowHeight = 650;

        const left = (screen.width - windowWidth) / 2;
        const top = (screen.height - windowHeight) / 2;

        return `location=0,status=0,titlebar=0,toolbar=0,width=${windowWidth},height=${windowHeight},resizable=yes,left=${left},top=${top}`;
    };

    const handleClickOnLogin = () => {
        const url = getAuthWindowUrl();
        const features = getAuthWindowFeatures();
        const authWindow = window.open(url, "_blank", features);

        setAuthWindow(authWindow);
    };

    const getCrmStatus = () => {
        const action = getCrmStatusMap[crm]?.();

        if (!action) {return;}

        dispatch(action);
    };

    useEffect(() => {
        if (!authWindow) {return;}

        const intervalTimeout = 1000;
        const interval = setInterval(() => {
            if (authWindow.closed) {
                clearInterval(interval);
                setAuthWindow(null);

                getCrmStatus();
                dispatch(fetchCrmListsThunk());
            }
        }, intervalTimeout);

        return () => clearInterval(interval);
    }, [authWindow]);

    const getDescription = () => {
        return translate("si.crm-integration.auth-required.description", { crmName });
    };

    return (
        <StyledUnmatchedEmail>
            <StyledUnmatchedEmailImageContainer>
                <img
                    alt="email not found"
                    src={AssetsService.assetUrl("/images/sales-intelligence/email-not-found.svg")}
                />
            </StyledUnmatchedEmailImageContainer>
            <div>
                <StyledCrmIntegratedDescription>{getDescription()}</StyledCrmIntegratedDescription>
                <Button
                    onClick={handleClickOnLogin}
                    isDisabled={!!authWindow}
                >
                    {translate("si.crm-integration.auth-required.button", { crmName })}
                </Button>
            </div>
        </StyledUnmatchedEmail>
    );
};
